import { ReactNode } from 'react'
import { RenderBalanceInColor } from '@/components/features/accounts/account-utils'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { BalanceTimelineChart } from '@/components/features/accounts/BalanceTimelineChart'
import { ArrowDown, ArrowUp, BarChart3, List, LucideIcon } from 'lucide-react'
import { MovementChartData } from '@/domain/accounts/account'

const formatDateRange = (dateFrom?: string, dateTo?: string): { from: string; to: string } => {
  return {
    from: dateFrom || '—',
    to: dateTo || '—',
  };
}

interface StatCardProps {
  title: string
  icon: LucideIcon
  value: ReactNode
  dateFrom: string
  dateTo: string
}

const StatCard = ({ title, icon: Icon, value, dateFrom, dateTo }: StatCardProps) => (
  <Card>
    <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
      <CardTitle className="text-sm font-medium">{title}</CardTitle>
      <Icon className="h-4 w-4 text-muted-foreground" />
    </CardHeader>
    <CardContent>
      <div className="text-2xl">{value}</div>
      <p className="text-xs mt-1">
        <span className="text-muted-foreground">Desde</span> {dateFrom} <span className="text-muted-foreground">al</span> {dateTo}
      </p>
    </CardContent>
  </Card>
)

interface AccountStatisticsProps {
  totalCredits: number
  totalDebits: number
  chartData: MovementChartData[]
  dateFrom?: string
  dateTo?: string
}

export const AccountStatistics = ({ totalCredits, totalDebits, chartData, dateFrom, dateTo }: AccountStatisticsProps) => {
  const { from, to } = formatDateRange(dateFrom, dateTo)

  return (
    <>
      <div className="grid gap-4 md:grid-cols-4">
        <StatCard
          title="Total Ingresos"
          icon={ArrowUp}
          value={<RenderBalanceInColor balance={totalCredits} />}
          dateFrom={from}
          dateTo={to}
        />
        <StatCard
          title="Total Egresos"
          icon={ArrowDown}
          value={<RenderBalanceInColor balance={-totalDebits} />}
          dateFrom={from}
          dateTo={to}
        />
        <StatCard
          title="Balance Neto"
          icon={BarChart3}
          value={<RenderBalanceInColor balance={totalCredits - totalDebits} />}
          dateFrom={from}
          dateTo={to}
        />
        <StatCard
          title="Total Movimientos"
          icon={List}
          value={chartData?.length || 0}
          dateFrom={from}
          dateTo={to}
        />
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Evolución del Saldo</CardTitle>
          <CardDescription>
            Historial de movimientos y balance acumulado
          </CardDescription>
        </CardHeader>
        <CardContent>
          <BalanceTimelineChart chartData={chartData} />
        </CardContent>
      </Card>
    </>
  )
}
